import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import 'animate.css';
import { TrashIcon } from '@heroicons/react/16/solid';

const AssistantPage = () => {
  const [chats, setChats] = useState([{ id: 1, messages: [] }]);
  const [currentChatId, setCurrentChatId] = useState(1);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  async function query(data) {
    const response = await fetch(
      'https://api.stack-ai.com/inference/v0/run/591ee433-5f39-47d8-9a4a-7489f13d6075/669ea269c74341c36c65ef34',
      {
        headers: {
          Authorization: 'Bearer 1bef0a7a-03ae-47f4-9b99-561c982df1cf',
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
    const result = await response.json();
    return result;
  }

  const handleSend = async () => {
    if (input.trim() !== '') {
      const newMessage = { text: input, sender: 'You' };
      const updatedChats = chats.map((chat) =>
        chat.id === currentChatId
          ? { ...chat, messages: [...chat.messages, newMessage] }
          : chat,
      );
      setChats(updatedChats);
      setInput('');
      setIsLoading(true);

      const response = await query({
        user_id: `${localStorage.getItem('g-accessToken')}`,
        'in-0': input,
      });

      const aiMessage = { text: response.outputs['out-1'], sender: 'SharkAI' };

      const updatedChatsWithAIResponse = updatedChats.map((chat) =>
        chat.id === currentChatId
          ? { ...chat, messages: [...chat.messages, aiMessage] }
          : chat,
      );

      setChats(updatedChatsWithAIResponse);
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSend();
    }
  };

  const handleChatSelection = (id) => {
    setCurrentChatId(id);
  };

  const handleNewChat = () => {
    const newChatId = chats.length + 1;
    setChats([
      ...chats,
      {
        id: newChatId,
        messages: [{ text: 'How can I help you?', sender: 'SharkAI' }],
      },
    ]);
    setCurrentChatId(newChatId);
  };

  const handleDeleteChat = (id) => {
    const updatedChats = chats.filter((chat) => chat.id !== id);
    setChats(updatedChats);
    if (currentChatId === id && updatedChats.length > 0) {
      setCurrentChatId(updatedChats[0].id);
    } else if (updatedChats.length === 0) {
      setChats([{ id: 1, messages: [] }]);
      setCurrentChatId(1);
    }
  };

  const currentChat = chats.find((chat) => chat.id === currentChatId);

  return (
    <div className="flex flex-col md:flex-row h-screen bg-gray-400">
      <div className="w-full md:w-1/4 bg-white shadow-md md:shadow-none md:border-r md:border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <button
            onClick={handleNewChat}
            className="w-full px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition duration-300"
          >
            New Chat
          </button>
        </div>
        <div className="p-4 space-y-2">
          {chats.map((chat) => (
            <div
              key={chat.id}
              className={`p-2 cursor-pointer flex items-center justify-between rounded-lg transition duration-300 ${
                chat.id === currentChatId
                  ? 'bg-black text-white'
                  : 'hover:bg-gray-800 hover:text-white'
              }`}
              onClick={() => handleChatSelection(chat.id)}
            >
              <span>Chat {chat.id}</span>
              <TrashIcon
                className="ml-2 w-6 h-6 hover:text-red-700 transition duration-300"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteChat(chat.id);
                }}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="flex-1 bg-white shadow-lg md:shadow-none md:border-l md:border-gray-200 flex flex-col">
        <div className="flex-1 p-4 overflow-y-auto border-b border-gray-200">
          {currentChat.messages.length === 0 ? (
            <div className="flex justify-center items-center h-full">
              <div className="text-center">
                <img
                  src="./gray_logo.svg"
                  alt="How can I help you?"
                  className="mx-auto mb-4"
                />
                <p className="text-lg font-semibold">
                  Hello! Myself SharkAI, How can I help you?
                </p>
              </div>
            </div>
          ) : (
            currentChat.messages.map((message, index) => (
              <div
                key={index}
                className={`flex mb-4 ${
                  message.sender === 'You' ? 'justify-end' : 'justify-start'
                } animate__animated animate__fadeIn`}
              >
                <div
                  className={`max-w-l p-3 rounded-lg shadow-md ${
                    message.sender === 'You'
                      ? 'bg-black text-white rounded-br-none'
                      : 'bg-gray-300 text-black rounded-bl-none'
                  }`}
                >
                  <span>
                    {message.sender === 'SharkAI' ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {message.text}
                      </ReactMarkdown>
                    ) : (
                      message.text
                    )}
                  </span>
                </div>
              </div>
            ))
          )}
          {isLoading && (
            <div className="flex justify-start mb-4 animate__animated animate__fadeIn">
              <div className="max-w-xs p-3 bg-black text-white rounded-lg shadow-md animate-pulse">
                <span>Generating Response...</span>
              </div>
            </div>
          )}
        </div>
        <div className="flex p-4 border-t border-gray-200">
          <input
            type="text"
            value={input}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            className="flex-1 p-2 border border-gray-300 rounded-lg mr-2 focus:outline-none focus:ring-2 focus:ring-black transition duration-300"
          />
          <button
            onClick={handleSend}
            className="px-4 py-2 bg-black text-white rounded-lg hover:bg-blue-600 transition duration-300"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssistantPage;
